import { useState } from 'react';
import { useContacts, useCreateContact } from '@/hooks/useCRM';
import { useAdminCustomers } from '@/hooks/useAdmin';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, Plus, Phone, Mail } from 'lucide-react';
import LeadScoreBadge from '@/components/crm/LeadScoreBadge';
import { toast } from 'sonner';

export default function ContactsPage() {
  const { data: contacts = [], isLoading } = useContacts();
  const { data: rawCustomers = [] } = useAdminCustomers();
  const customers = Array.isArray(rawCustomers) ? rawCustomers.filter(c => c.is_active && c.id && (c.company || c.name)) : [];
  const createContact = useCreateContact();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', title: '', customer_id: '', customer_name: '' });

  const filtered = contacts.filter(c =>
    `${c.first_name} ${c.last_name} ${c.email} ${c.customer_name}`.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = () => {
    if (!form.first_name || !form.last_name) {
      toast.error('First and last name are required');
      return;
    }
    createContact.mutate(form, {
      onSuccess: () => {
        toast.success('Contact created');
        setCreateOpen(false);
        setForm({ first_name: '', last_name: '', email: '', phone: '', title: '', customer_id: '', customer_name: '' });
      },
      onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to create contact'),
    });
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Contacts" subtitle="Manage customer contacts and relationships">
        <Button className="gap-2" onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> Add Contact</Button>
      </PageHeader>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-3">
          {filtered.map(contact => (
            <Card
              key={contact.id}
              className="cursor-pointer transition-colors hover:bg-accent/50"
              onClick={() => navigate(`/crm/contacts/${contact.id}`)}
            >
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {contact.first_name[0]}{contact.last_name[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                  <p className="text-xs text-muted-foreground">{contact.title} — {contact.customer_name}</p>
                </div>
                <div className="hidden items-center gap-3 sm:flex">
                  {contact.phone && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{contact.phone}</span>
                  )}
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{contact.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  {contact.tags.slice(0, 2).map(t => (
                    <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                  ))}
                  <LeadScoreBadge score={contact.lead_score} />
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No contacts found</p>}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Contact</DialogTitle>
            <DialogDescription>Add a new contact to the CRM.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>First Name *</Label>
                <Input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} placeholder="John" />
              </div>
              <div className="space-y-1">
                <Label>Last Name *</Label>
                <Input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} placeholder="Smith" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" />
            </div>
            <div className="space-y-1">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="555-0100" />
            </div>
            <div className="space-y-1">
              <Label>Title / Role</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="IT Director" />
            </div>
            <div className="space-y-1">
              <Label>Customer *</Label>
              <Select
                value={form.customer_id}
                onValueChange={val => {
                  const cust = customers.find(c => c.id === val);
                  setForm(p => ({ ...p, customer_id: val, customer_name: cust?.company || cust?.name || '' }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createContact.isPending}>Create Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
